/**
 * The source page of a quoted clause, with the quote marked.
 *
 * The clause reference carries a page number, but a scanned contract is often
 * numbered by its printed page rather than by the PDF's own page count, so the
 * quote is searched for in the text layer first and the stated page is only the
 * fallback. Scans have no text layer at all, which the viewer says plainly
 * rather than silently showing page one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { ChevronLeft, ChevronRight, Loader2, Minus, Plus, ScanLine } from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const norm = (s) => String(s || '')
  .replace(/[“”"’']/g, '')
  .replace(/[^a-z0-9%.()\s]/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

export function ClauseHighlightPdf({ url, quote, hintPage, fileName }) {
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(hintPage || 1);
  const [scale, setScale] = useState(1.1);
  const [state, setState] = useState('loading');   // loading | found | hint | notext | plain
  const matchPage = useRef(0);

  const needle = useMemo(() => {
    const n = norm(quote);
    return n.length > 24 ? n : '';
  }, [quote]);
  const shortNeedle = useMemo(() => needle.split(' ').slice(0, 6).join(' '), [needle]);

  useEffect(() => { setPage(hintPage || 1); }, [hintPage]);

  const onLoad = useCallback(async (pdf) => {
    setNumPages(pdf.numPages);
    if (!needle) { setState(hintPage ? 'hint' : 'plain'); return; }
    const order = [];
    if (hintPage && hintPage <= pdf.numPages) order.push(hintPage);
    for (let i = 1; i <= Math.min(pdf.numPages, 150); i += 1) if (i !== hintPage) order.push(i);
    let anyText = false;
    for (const p of order) {
      let text = '';
      try {
        const content = await pdf.getPage(p).then(pg => pg.getTextContent());  // eslint-disable-line no-await-in-loop
        text = norm(content.items.map(i => i.str).join(' '));
      } catch { text = ''; }
      if (text.length > 40) anyText = true;
      if (text && (text.includes(needle) || (shortNeedle.length > 12 && text.includes(shortNeedle)))) {
        matchPage.current = p;
        setPage(p);
        setState('found');
        return;
      }
    }
    matchPage.current = 0;
    setState(anyText ? (hintPage ? 'hint' : 'plain') : 'notext');
  }, [needle, shortNeedle, hintPage]);

  const renderText = useCallback(({ str }) => {
    if (!needle || page !== matchPage.current) return str;
    const n = norm(str);
    if (n.length >= 6 && needle.includes(n)) {
      return `<mark style="background:#a855f7;color:#fff;padding:0 1px;border-radius:2px">${str}</mark>`;
    }
    return str;
  }, [needle, page]);

  return (
    <div className="h-full flex flex-col bg-neutral-900/40" data-testid="clause-highlight-pdf">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-background/60">
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1 rounded hover:bg-muted disabled:opacity-30" data-testid="clause-pdf-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-[11px] font-mono text-muted-foreground" data-testid="clause-pdf-page">
          {page} / {numPages || '—'}
        </span>
        <button onClick={() => setPage(p => Math.min(numPages || p, p + 1))} disabled={numPages ? page >= numPages : true}
                className="p-1 rounded hover:bg-muted disabled:opacity-30" data-testid="clause-pdf-next">
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="mx-1 h-4 w-px bg-border" />
        <button onClick={() => setScale(s => Math.max(0.5, +(s - 0.15).toFixed(2)))}
                className="p-1 rounded hover:bg-muted" data-testid="clause-pdf-zoom-out">
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span className="text-[11px] font-mono text-muted-foreground w-10 text-center">{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale(s => Math.min(2.5, +(s + 0.15).toFixed(2)))}
                className="p-1 rounded hover:bg-muted" data-testid="clause-pdf-zoom-in">
          <Plus className="w-3.5 h-3.5" />
        </button>
        <div className="ml-auto text-[10px] text-muted-foreground" data-testid="clause-pdf-state">
          {state === 'loading' && <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> finding the clause…</span>}
          {state === 'found' && <span className="text-purple-400">quote found on this page and marked</span>}
          {state === 'hint' && <span className="text-amber-500">opened at the page the clause cites — the quote itself was not found in the text layer</span>}
          {state === 'notext' && (
            <span className="flex items-center gap-1 text-amber-500">
              <ScanLine className="w-3 h-3" /> scanned page — opened where the clause was read from; the words cannot be marked on an image
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-auto flex justify-center py-4">
        <Document file={url} onLoadSuccess={onLoad}
                  loading={<div className="text-xs text-muted-foreground pt-10">Opening {fileName}…</div>}
                  error={<div className="text-xs text-red-400 pt-10">This document could not be opened in the viewer.</div>}>
          <Page pageNumber={page} scale={scale} customTextRenderer={renderText}
                renderAnnotationLayer={false}
                className="shadow-lg" />
        </Document>
      </div>
    </div>
  );
}

export default ClauseHighlightPdf;
